import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { addDoc, deleteDoc, doc, getDocs, updateDoc } from 'firebase/firestore'
import { Formula } from '@otw/models'
import { formulaConverter } from '@otw/models/converters'
import { db } from '@otw/models/db'

interface UseFormulasOptions {
  onSuccess?: () => void
  onError?: (error: Error) => void
  dontShowUpdates?: boolean
}

interface UseFormulasParams {
  options?: UseFormulasOptions
}

export const useFormulas = ({ options = {} }: UseFormulasParams) => {
  const { showSuccess, showError, showInfo, showWarning } = useSnackbar()
  const queryClient = useQueryClient()
  const { userInfo } = useAuth()

  const formulasCollectionRef = db.formulas

  const queryKey = ['gray_book_formulas']

  const {
    data: formulas,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<Formula[]>({
    queryKey,
    queryFn: async () => {
      const collectionWithConverter = formulasCollectionRef.withConverter(formulaConverter)
      const snapshot = await getDocs(collectionWithConverter)
      return snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
      }))
    },
    select: data => data.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const addFormula = useMutation({
    mutationFn: async (formula: Omit<Formula, 'id' | 'createdAt' | 'updatedAt'>) => {
      const newFormula = {
        ...formula,
        createdBy: userInfo?.email || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as Formula

      const collectionWithConverter = formulasCollectionRef.withConverter(formulaConverter)
      const docRef = await addDoc(collectionWithConverter, formulaConverter.toFirestore(newFormula))
      const addedFormula = {
        ...newFormula,
        id: docRef.id,
      } as Formula
      return addedFormula
    },
    onMutate: async newFormula => {
      await queryClient.cancelQueries({ queryKey })
      const previousFormulas = queryClient.getQueryData<Formula[]>(queryKey)

      const optimisticFormula = {
        ...newFormula,
        id: `temp-${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: userInfo?.email || '',
      }

      queryClient.setQueryData<Formula[]>(queryKey, old => {
        return [...(old || []), optimisticFormula]
      })

      return { previousFormulas, optimisticFormula }
    },
    onError: (err, newFormula, context) => {
      queryClient.setQueryData(queryKey, context?.previousFormulas)
      if (!options?.dontShowUpdates) {
        showError('Error adding formula')
      }
      console.error('Error adding formula:', err)
      options?.onError?.(err)
    },
    onSuccess: (result, variables, context) => {
      queryClient.setQueryData<Formula[]>(queryKey, old => {
        return old?.map(formula => (formula.id === context?.optimisticFormula.id ? result : formula))
      })
      if (!options?.dontShowUpdates) {
        showSuccess('Formula added successfully')
      }
      options?.onSuccess?.()
    },
  })

  const updateFormula = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Formula> }) => {
      const collectionWithConverter = formulasCollectionRef.withConverter(formulaConverter)
      const formulaRef = doc(collectionWithConverter, id)
      const updateData = formulaConverter.toFirestore({
        ...data,
        updatedAt: new Date().toISOString(),
      } as Formula)
      await updateDoc(formulaRef, updateData)
      return { id, ...updateData }
    },
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey })
      const previousFormulas = queryClient.getQueryData<Formula[]>(queryKey)

      queryClient.setQueryData<Formula[]>(queryKey, old => {
        if (!old) return old
        return old.map(formula => (formula.id === id ? { ...formula, ...data } : formula))
      })

      return { previousFormulas }
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(queryKey, context?.previousFormulas)
      if (!options?.dontShowUpdates) {
        showError('Error updating formula')
      }
      console.error('Error updating formula:', err)
      options?.onError?.(err)
    },
    onSuccess: result => {
      if (!options?.dontShowUpdates) {
        showSuccess('Formula updated successfully')
      }
      options?.onSuccess?.()
    },
  })

  const deleteFormula = useMutation({
    mutationFn: async (id: string) => {
      const collectionWithConverter = formulasCollectionRef.withConverter(formulaConverter)
      const formulaRef = doc(collectionWithConverter, id)
      // Get the current formula data before deleting
      const currentFormulas = queryClient.getQueryData<Formula[]>(queryKey)
      const formulaToDelete = currentFormulas?.find(f => f.id === id)
      
      await deleteDoc(formulaRef)
      return { id, data: formulaToDelete }
    },
    onMutate: async id => {
      await queryClient.cancelQueries({ queryKey })
      const previousFormulas = queryClient.getQueryData<Formula[]>(queryKey)

      queryClient.setQueryData<Formula[]>(queryKey, old => {
        if (!old) return old
        return old.filter(formula => formula.id !== id)
      })

      return { previousFormulas }
    },
    onError: (err, id, context) => {
      queryClient.setQueryData(queryKey, context?.previousFormulas)
      if (!options?.dontShowUpdates) {
        showError('Error deleting formula')
      }
      console.error('Error deleting formula:', err)
      options?.onError?.(err)
    },
    onSuccess: () => {
      if (!options?.dontShowUpdates) {
        showSuccess('Formula deleted successfully')
      }
      options?.onSuccess?.()
    },
  })

  return {
    formulas,
    isLoading,
    isError,
    error,
    refetchFormulas: refetch,
    addFormula: addFormula.mutateAsync,
    updateFormula: updateFormula.mutateAsync,
    deleteFormula: deleteFormula.mutateAsync,
  }
}
